import { Link } from 'react-router-dom';
import { getNavProducts } from '../assets/products';
import { useI18n } from '../i18n';

const Footer = () => {
  const { t, locale, localePath } = useI18n();
  const products = getNavProducts(locale);
  const productColumns = [products.slice(0, 6), products.slice(6)];

  return (
    <footer className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200/70">
      <div className="max-w-screen-xl mx-auto py-14 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-10">
          <div className="space-y-7 xl:col-span-1">
            <Link to={localePath('/')}>
              <img className="h-16" src="/images/akas-logo.png" alt="AKAS" />
            </Link>
            <p className="text-gray-500 text-base leading-6 max-w-sm">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-2">
              <a href="https://facebook.com/www.akas.co" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary bg-white border border-slate-200 rounded-full p-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                <span className="sr-only">Facebook</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://linkedin.com/company/akas-co" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary bg-white border border-slate-200 rounded-full p-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a href="https://docs.akas.co/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-primary bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md">
                Docs
              </a>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              {productColumns.map((column, columnIndex) => (
                <div key={columnIndex} className={columnIndex === 1 ? 'mt-12 md:mt-0' : undefined}>
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                    {columnIndex === 0 ? t.footer.modules : t.footer.moreModules}
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {column.map((product) => (
                      <li key={product.slug}>
                        <Link to={localePath(`/products/${product.slug}`)} className="text-base text-gray-500 hover:text-primary">
                          {product.navLabel || product.shortName}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t.footer.company}
                </h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to={localePath('/about-us')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.aboutUs}
                    </Link>
                  </li>
                  <li>
                    <Link to={localePath('/solutions')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.solutions}
                    </Link>
                  </li>
                  <li>
                    <Link to={localePath('/demo')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.liveDemos}
                    </Link>
                  </li>
                  <li>
                    <Link to={localePath('/portfolios')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.portfolios}
                    </Link>
                  </li>
                  <li>
                    <Link to={localePath('/blog')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.blogs}
                    </Link>
                  </li>
                  <li>
                    <Link to={localePath('/contact')} className="text-base text-gray-500 hover:text-primary">
                      {t.footer.contact}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t.footer.contact}
                </h3>
                <ul className="mt-4 space-y-4">
                  <li className="flex">
                    <svg className="flex-shrink-0 h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="ml-3 text-base text-gray-500">
                      <a href="tel:+85510440600">+855 10 440 600</a>
                    </span>
                  </li>
                  <li className="flex">
                    <svg className="flex-shrink-0 h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="ml-3 text-base text-gray-500">
                      <a href="mailto:info@akas.co">info@akas.co</a>
                    </span>
                  </li>
                  <li className="flex">
                    <svg className="flex-shrink-0 h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="ml-3 text-base text-gray-500">
                      {t.footer.location}
                    </span>
                  </li>
                  
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-14 border-t border-slate-200/80 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-400 text-center sm:text-left">
            &copy; {new Date().getFullYear()} AKAS. {t.footer.allRightsReserved}
          </p>
          <p className="text-sm text-gray-400 text-center sm:text-right">
            {t.footer.builtBy}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
